import { File05Icon } from '@langchain/untitled-ui-icons';

import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import { Pane } from '@/components/Pane';
import { Button, Text } from '@/design-system';
import { useSegmentEventWrapper } from '@/hooks/useSegmentEventWrapper';

type QuickstartCardProps = {
  title: string;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  segmentEventButtonName: string;
} & ({ to: string } | { paneContent: ReactNode; docsLink: string });

export function QuickstartCard({
  title,
  leftContent,
  rightContent,
  segmentEventButtonName,
  ...rest
}: QuickstartCardProps) {
  const [open, setOpen] = useState(false);

  const track = useSegmentEventWrapper(() => {
    return [
      'home_get_started_button_click',
      {
        button_name: segmentEventButtonName,
      },
    ];
  });

  const handleClick = track(() => {
    if ('to' in rest) {
      return;
    }

    setOpen(true);
  });

  const cardContent = (
    <div className="flex h-[50px] items-center rounded-xl border border-secondary px-5 shadow-[2px_5px_38px_0px_#2A2C3C0D,4px_4px_4px_0px_#2A2C3C05] hover:bg-secondary 2xl:h-[80px] 2xl:px-7">
      <div className="flex flex-grow items-center gap-2 2xl:gap-4">
        {leftContent}
        <div className="flex flex-grow items-center gap-4 2xl:gap-6">
          <Text variant="h3" className="font-medium">
            {title}
          </Text>
          {rightContent}
        </div>
      </div>
    </div>
  );

  return 'to' in rest ? (
    <Link to={rest.to} onClick={handleClick} className="w-full">
      {cardContent}
    </Link>
  ) : (
    <>
      <button type="button" onClick={handleClick} className="w-full">
        {cardContent}
      </button>
      <Pane
        title={title}
        topBarRightElement={
          <Link to={rest.docsLink} target="_blank">
            <Button
              leftDecorator={File05Icon}
              color="secondary"
              variant="outlined"
              size="sm"
              className="mr-6"
            >
              Docs
            </Button>
          </Link>
        }
        open={open}
        onClose={() => setOpen(false)}
      >
        {rest.paneContent}
      </Pane>
    </>
  );
}
